import React from 'react';
import { arrayOf, bool, func, node, number, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import { withViewport } from '../../../../util/uiHelpers';

import { IconBillCheck, IconSchedule, IconPeople, IconSupportChat } from '../../../../components';
import css from './SectionPerks.module.css';

// The number of columns (numColumns) affects styling and responsive images
const COLUMN_CONFIG = [
  { css: css.oneColumn, responsiveImageSizes: '(max-width: 767px) 100vw, 1200px' },
  { css: css.twoColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 600px' },
  { css: css.threeColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 400px' },
  { css: css.fourColumns, responsiveImageSizes: '(max-width: 767px) 100vw, 265px' },
];
const getIndex = numColumns => numColumns - 1;
const getColumnCSS = numColumns => {
  const config = COLUMN_CONFIG[getIndex(numColumns)];
  return config ? config.css : COLUMN_CONFIG[0].css;
};

// Section component that's able to show blocks in multiple different columns (defined by "numColumns" prop)
const SectionPerks = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    blocks,
    isInsideContainer,
    options,
    viewport,
    intl,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  return (
    <div className={css.mainContainer}>
      <SectionContainer
        id={sectionId}
        className={classNames(className, css.perks)}
        rootClassName={rootClassName}
        appearance={appearance}
        options={fieldOptions}
      >
        {hasHeaderFields ? (
          <header className={classNames(css.sectionDetails, defaultClasses.sectionDetails)}>
            <Field
              data={title}
              className={classNames(css.title, defaultClasses.title)}
              options={fieldOptions}
            />
            <Field
              data={description}
              className={classNames(css.description, defaultClasses.description)}
              options={fieldOptions}
            />
            <div className={css.ctaContainer}>
              <Field
                data={callToAction}
                className={classNames(defaultClasses.ctaButton, css.ctaButton, css.secondaryCta)}
                options={fieldOptions}
              />
            </div>
          </header>
        ) : null}

        <div
          className={classNames(
            defaultClasses.blockContainer,
            css.blockContainer,
            getColumnCSS(numColumns),
            {
              [css.noSidePaddings]: isInsideContainer,
            }
          )}
        >
          <div className={css.block}>
            <div className={css.blockNumber}>
              <IconBillCheck />
            </div>
            <div>
              <div className={css.blockTitle}>
                {intl.formatMessage({
                  id: 'LandingPage.SectionPerks.perk1.title',
                })}
              </div>
              <div className={css.blockDetail}>
                {intl.formatMessage({
                  id: 'LandingPage.SectionPerks.perk1.details',
                })}
              </div>
            </div>
          </div>
          <div className={css.block}>
            <div className={css.blockNumber}>
              <IconSchedule />
            </div>
            <div>
              <div className={css.blockTitle}>
                {intl.formatMessage({
                  id: 'LandingPage.SectionPerks.perk2.title',
                })}
              </div>
              <div className={css.blockDetail}>
                {intl.formatMessage({
                  id: 'LandingPage.SectionPerks.perk2.details',
                })}
              </div>
            </div>
          </div>
          <div className={css.block}>
            <div className={css.blockNumber}>
              <IconPeople />
            </div>
            <div>
              <div className={css.blockTitle}>
                {intl.formatMessage({
                  id: 'LandingPage.SectionPerks.perk3.title',
                })}
              </div>
              <div className={css.blockDetail}>
                {intl.formatMessage({
                  id: 'LandingPage.SectionPerks.perk3.details',
                })}
              </div>
            </div>
          </div>
          <div className={css.block}>
            <div className={css.blockNumber}>
              <IconSupportChat />
            </div>
            <div>
              <div className={css.blockTitle}>
                {intl.formatMessage({
                  id: 'LandingPage.SectionPerks.perk4.title',
                })}
              </div>
              <div className={css.blockDetail}>
                {intl.formatMessage({
                  id: 'LandingPage.SectionPerks.perk4.details',
                })}
              </div>
            </div>
          </div>
        </div>
      </SectionContainer>
    </div>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionPerks.defaultProps = {
  className: null,
  rootClassName: null,
  defaultClasses: null,
  textClassName: null,
  numColumns: 1,
  title: null,
  description: null,
  appearance: null,
  callToAction: null,
  blocks: [],
  isInsideContainer: false,
  options: null,
};

SectionPerks.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  defaultClasses: shape({
    sectionDetails: string,
    title: string,
    description: string,
    ctaButton: string,
  }),
  numColumns: number,
  title: object,
  description: object,
  appearance: object,
  callToAction: object,
  blocks: arrayOf(object),
  isInsideContainer: bool,
  options: propTypeOption,
};

export default withViewport(SectionPerks);
